import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function SubscriptionDetail() {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [activeProducts, setActiveProducts] = useState([]);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    fetchData();
  }, [subscriptionId]);

  const fetchData = async () => {
    try {
      const [subsRes, companiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/subscriptions`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/companies`, { credentials: 'include' })
      ]);

      if (subsRes.ok && companiesRes.ok) {
        const subsData = await subsRes.json();
        const companiesData = await companiesRes.json();
        
        const sub = subsData.find(s => s.subscription_id === subscriptionId);
        if (sub) {
          setSubscription(sub);
          setActiveProducts(sub.active_products || sub.products || []);
          const comp = companiesData.find(c => c.company_id === sub.company_id);
          setCompany(comp);
        }
      }
    } catch (error) {
      toast.error('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProduct = (product) => {
    if (activeProducts.includes(product)) {
      setActiveProducts(activeProducts.filter(p => p !== product));
    } else {
      setActiveProducts([...activeProducts, product]);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/${subscriptionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active_products: activeProducts })
      });

      if (response.ok) {
        toast.success('Products updated successfully');
        navigate('/admin/subscriptions');
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update products');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Subscription Details">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!subscription) {
    return (
      <AdminLayout title="Subscription Details">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Subscription not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Manage Products">
      <div className="max-w-4xl space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/subscriptions')}
            className="font-mono"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-mono font-bold text-secondary mb-2">
              {company?.name || 'Company'}
            </h2>
            <p className="text-muted-foreground font-mono">
              Cyber {subscription.tier.toUpperCase()} • {subscription.status}
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-mono font-bold">Active Products</h3>
            <p className="text-sm text-muted-foreground">
              Select which products are active for this customer
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {subscription.products.map((product, index) => {
                const isActive = activeProducts.includes(product);
                return (
                  <div
                    key={index}
                    onClick={() => handleToggleProduct(product)}
                    className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition-all ${
                      isActive
                        ? 'bg-primary/10 border-primary/50'
                        : 'bg-background border-border hover:border-border/80'
                    }`}
                  >
                    <Checkbox
                      checked={isActive}
                      onCheckedChange={() => handleToggleProduct(product)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className={`text-sm font-mono ${isActive ? 'font-bold text-primary' : ''}`}>
                        {product}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground font-mono">
                {activeProducts.length} of {subscription.products.length} products active
              </p>
              <Button
                onClick={handleSave}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-mono"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default SubscriptionDetail;
